import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { FaTimes, FaCheck } from "react-icons/fa";
import { IoMdArrowRoundForward } from "react-icons/io";
import { SEND_SPONSOR_INQUIRY_URL } from "../data/EventConfig";

const SPONSOR_INBOX = "clubrotaract.timisoara@gmail.com";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(10, 42, 74, 0.6);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  animation: ${fadeIn} 0.2s ease;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 20px;
  width: 100%;
  max-width: 540px;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.35);
  animation: ${slideUp} 0.28s cubic-bezier(0.22, 0.61, 0.36, 1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 2rem);
`;

const Header = styled.div`
  background: linear-gradient(135deg, #ffce2d 0%, #faa71e 100%);
  padding: 1.5rem 1.5rem 1.25rem;
  position: relative;
`;

const Title = styled.h2`
  color: #0a2a4a;
  font-size: 1.4rem;
  font-weight: 800;
  margin: 0 0 0.35rem;
`;

const Sub = styled.p`
  color: rgba(10, 42, 74, 0.78);
  font-size: 0.92rem;
  margin: 0;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(10, 42, 74, 0.12);
  color: #0a2a4a;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(10, 42, 74, 0.22);
  }
`;

const Body = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
`;

const Label = styled.label`
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #0a2a4a;
  margin-bottom: 0.5rem;
`;

const fieldStyles = `
  width: 100%;
  border: 1px solid rgba(10, 42, 74, 0.18);
  border-radius: 12px;
  padding: 0.75rem 0.9rem;
  font-size: 0.95rem;
  color: #0a2a4a;
  outline: none;
  margin-bottom: 0.85rem;
  transition: border-color 0.15s ease;
  font-family: inherit;
  background: #fff;

  &:focus {
    border-color: #faa71e;
  }
`;

const Field = styled.input`
  ${fieldStyles}
`;

const TextArea = styled.textarea`
  ${fieldStyles}
  min-height: 120px;
  resize: vertical;
`;

const Honeypot = styled.input`
  position: absolute;
  left: -10000px;
  top: auto;
  width: 1px;
  height: 1px;
  overflow: hidden;
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 0.75rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

const Submit = styled.button`
  width: 100%;
  height: 52px;
  border: none;
  border-radius: 999px;
  background: #faa71e;
  color: #fff;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 12px 24px rgba(250, 167, 30, 0.35);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  margin-top: 0.5rem;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 16px 28px rgba(250, 167, 30, 0.45);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const ErrorBox = styled.div`
  background: rgba(220, 38, 38, 0.08);
  border: 1px solid rgba(220, 38, 38, 0.3);
  color: #b91c1c;
  font-size: 0.86rem;
  padding: 0.6rem 0.85rem;
  border-radius: 10px;
  margin-bottom: 0.85rem;
`;

const SuccessBox = styled.div`
  text-align: center;
  padding: 1rem 0.5rem 0.25rem;

  svg {
    color: #1a7a3a;
  }

  h3 {
    color: #0a2a4a;
    font-size: 1.2rem;
    font-weight: 800;
    margin: 0.75rem 0 0.5rem;
  }

  p {
    color: #2d3e56;
    font-size: 0.95rem;
    line-height: 1.5;
    margin: 0;
  }
`;

const SuccessIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(26, 122, 58, 0.12);
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const Note = styled.p`
  font-size: 0.78rem;
  color: rgba(10, 42, 74, 0.6);
  text-align: center;
  margin: 0.75rem 0 0;
`;

const SponsorModal = ({ isOpen, onClose }) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [value, setValue] = useState("");
  const [description, setDescription] = useState("");
  const [website, setWebsite] = useState(""); // honeypot
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setError("");
      setSubmitting(false);
      setDone(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) {
      setError("Spune-ne cum te numești sau numele companiei.");
      return;
    }
    if (!description.trim()) {
      setError("Descrie pe scurt cum ai dori să sprijini cursa.");
      return;
    }
    if (!email.trim() && !phone.trim()) {
      setError("Lasă-ne un email sau un număr de telefon ca să te contactăm.");
      return;
    }

    if (!SEND_SPONSOR_INQUIRY_URL) {
      const lines = [
        `Nume / Companie: ${name}`,
        email ? `Email: ${email}` : null,
        phone ? `Telefon: ${phone}` : null,
        value ? `Valoare estimată: ${value}` : null,
        "",
        "Descriere:",
        description,
      ].filter(Boolean);
      const subject = encodeURIComponent(`Cerere de sponsorizare — ${name}`);
      const bodyText = encodeURIComponent(lines.join("\n"));
      window.location.href = `mailto:${SPONSOR_INBOX}?subject=${subject}&body=${bodyText}`;
      return;
    }

    setSubmitting(true);
    try {
      const resp = await fetch(SEND_SPONSOR_INQUIRY_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          phone,
          value,
          description,
          website, // honeypot
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.error || "Nu am putut trimite cererea.");
      }
      setDone(true);
    } catch (err) {
      setError(err.message || "A apărut o eroare. Încearcă din nou.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Backdrop onClick={onClose}>
      <Card onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>Devino sponsor</Title>
          <Sub>
            Spune-ne câte ceva despre tine — revenim cu un răspuns cât de
            curând.
          </Sub>
          <CloseBtn onClick={onClose} aria-label="Închide">
            <FaTimes size={14} />
          </CloseBtn>
        </Header>

        <Body>
          {done ? (
            <SuccessBox>
              <SuccessIcon>
                <FaCheck size={22} />
              </SuccessIcon>
              <h3>Mulțumim!</h3>
              <p>
                Cererea ta a ajuns la Rotaract Club Timișoara. Te contactăm în
                cel mai scurt timp posibil.
              </p>
              <Submit type="button" onClick={onClose} style={{ marginTop: "1.5rem" }}>
                Închide
              </Submit>
            </SuccessBox>
          ) : (
            <form onSubmit={submit} noValidate>
              {error && <ErrorBox>{error}</ErrorBox>}

              <Honeypot
                type="text"
                tabIndex="-1"
                autoComplete="off"
                aria-hidden="true"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                name="website"
              />

              <Label htmlFor="sp-name">Nume / Companie</Label>
              <Field
                id="sp-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ex: SC Exemplu SRL sau Ana Popescu"
                maxLength={200}
                required
              />

              <TwoCol>
                <div>
                  <Label htmlFor="sp-email">Email</Label>
                  <Field
                    id="sp-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="contact@exemplu.ro"
                    maxLength={200}
                  />
                </div>
                <div>
                  <Label htmlFor="sp-phone">Telefon</Label>
                  <Field
                    id="sp-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="07XX XXX XXX"
                    maxLength={50}
                  />
                </div>
              </TwoCol>

              <Label htmlFor="sp-value">Valoare estimată (RON sau în natură)</Label>
              <Field
                id="sp-value"
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Ex: 1500 RON sau echipament audio"
                maxLength={100}
              />

              <Label htmlFor="sp-desc">Cu ce ai vrea să ne sprijini?</Label>
              <TextArea
                id="sp-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descrie pe scurt — produse, servicii, premii, expunere etc."
                maxLength={4000}
                required
              />

              <Submit type="submit" disabled={submitting}>
                {submitting ? "Se trimite..." : "Trimite cererea"}
                {!submitting && <IoMdArrowRoundForward />}
              </Submit>

              <Note>
                Cererea ajunge direct la Rotaract Club Timișoara
                ({SPONSOR_INBOX}).
              </Note>
            </form>
          )}
        </Body>
      </Card>
    </Backdrop>
  );
};

export default SponsorModal;
